import { ASSETS, CONTACT, LICENSED_STATES } from "@/lib/constants";
import { useMLO } from "@/lib/mloContext";

export default function Footer() {
  const year = new Date().getFullYear();
  const mlo = useMLO();
  const full = mlo?.name || CONTACT.loanOfficer;
  const first = full.split(" ")[0];
  const nmls = mlo?.nmls || CONTACT.nmls;
  const phone = mlo?.phone || CONTACT.phoneDisplay;
  const phoneHref = mlo?.phone ? `tel:${mlo.phone.replace(/[^+0-9]/g, "")}` : CONTACT.phoneHref;
  const email = mlo ? (mlo.email || "") : CONTACT.email;
  const emailHref = mlo ? (mlo.email ? `mailto:${mlo.email}` : "") : CONTACT.emailHref;
  const city = mlo ? (mlo.city || "") : CONTACT.city;
  return (
    <footer data-testid="site-footer" className="bg-navy-deep text-white/80">
      <div className="container-grc py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="inline-flex items-center rounded-2xl bg-white/95 px-3 py-2 shadow-soft">
              <img
                src={ASSETS.logoHorizontal}
                alt="Great Rate Check"
                className="h-10 w-auto"
              />
            </div>
            <p className="mt-5 text-sm text-white/70 max-w-md leading-relaxed">
              Great Rate Check is a marketing platform for mortgage services
              provided by {full}, a licensed loan officer with NEXA Mortgage LLC.
              We help homeowners and buyers compare personalized rates and connect
              with an experienced loan advisor.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <span className="inline-flex items-center gap-2 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full text-[11px] font-medium">
                <span className="w-2 h-2 rounded-full bg-lime" /> Equal Housing Lender
              </span>
              <span className="inline-flex items-center gap-2 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full text-[11px] font-medium">
                NMLS Consumer Access
              </span>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs font-bold tracking-[0.22em] uppercase text-lime">Contact</p>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <a data-testid="footer-phone" className="hover:text-lime" href={phoneHref}>
                  {phone}
                </a>
              </li>
              {email && (
                <li>
                  <a data-testid="footer-email" className="hover:text-lime" href={emailHref}>
                    {email}
                  </a>
                </li>
              )}
              {city && <li className="text-white/60">{city}</li>}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-xs font-bold tracking-[0.22em] uppercase text-lime">Explore</p>
            <ul className="mt-4 space-y-2 text-sm">
              <li><a className="hover:text-lime" href="#why" data-testid="footer-link-why">Why check your rate</a></li>
              <li><a className="hover:text-lime" href="#how" data-testid="footer-link-how">How it works</a></li>
              <li><a className="hover:text-lime" href="#jason" data-testid="footer-link-jason">Meet {first}</a></li>
              <li><a className="hover:text-lime" href="#faq" data-testid="footer-link-faq">FAQ</a></li>
              <li>
                <a
                  className="hover:text-lime"
                  href={`https://www.nmlsconsumeraccess.org/EntityDetails.aspx/INDIVIDUAL/${nmls}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-nmls-verify"
                >
                  Verify NMLS #{nmls}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 space-y-4 text-[11.5px] leading-relaxed text-white/60">
          <p data-testid="footer-disclosure-nmls">
            <strong className="text-white/80">Licensing:</strong> {full}, NMLS #{nmls}, is a licensed mortgage loan
            originator with NEXA Mortgage, LLC (NMLS #{CONTACT.companyNmls}). NEXA Mortgage,
            LLC is an Equal Housing Lender. 3100 W Ray Rd Suite 201, Office 209, Chandler, AZ 85226.
          </p>
          <p data-testid="footer-disclosure-states">
            <strong className="text-white/80">Licensed states (partial):</strong> {LICENSED_STATES}. Additional states may apply. Verify licensing at{" "}
            <a
              className="underline hover:text-lime"
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.nmlsconsumeraccess.org/"
            >
              nmlsconsumeraccess.org
            </a>.
          </p>
          <p data-testid="footer-disclosure-rates">
            <strong className="text-white/80">Rate disclosure:</strong> Advertised
            rates and payments displayed on this site are illustrative and not a
            commitment to lend. Actual rates, APRs, and payments depend on
            individual credit profile, loan amount, loan-to-value ratio, property
            type, occupancy, program, and prevailing market conditions.
          </p>
          <p data-testid="footer-disclosure-privacy">
            <strong className="text-white/80">Privacy:</strong> Information you
            submit is used exclusively to prepare a personalized rate scenario and
            to follow up with you. We do not sell your information to third
            parties.
          </p>

          <p data-testid="footer-disclosure-nexa" className="pt-2">
            <strong className="text-white/80">NEXA Disclaimer:</strong> This is
            not an offer to enter into an agreement. This is not a commitment to
            make a loan. Not all customers will qualify. Information, rates and
            programs are subject to change without prior notice. All products are
            subject to credit and property approval. All approvals are subject to
            underwriting guidelines. Not all products are available in all states
            or for all dollar amounts. Other restrictions and limitations may
            apply. VA loans subject to individual VA Entitlement amounts and
            eligibility, qualifying factors such as income and credit standards,
            and property limits. NEXA Mortgage, LLC is not affiliated with any
            government agencies. These materials are not from VA, HUD or FHA, and
            were not approved by VA, HUD or FHA, or any other government agency.
            Copyright © 2020 NEXA Mortgage, LLC NMLS #1660690. AZBK #2006218.
            5559 S Sossaman Rd Bldg #1 Ste #101 Mesa, AZ 85212, 602-344-9333.{" "}
            <a
              className="underline hover:text-lime"
              href="https://www.NEXAMortgage.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.NEXAMortgage.com
            </a>
            . NEXA Mortgage, LLC is an Equal Housing Lender. All rights reserved.
          </p>

          <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-white/50">© {year} Great Rate Check. All rights reserved.</p>
            <div className="flex items-center gap-4 text-white/50">
              <a
                href="/mlologin"
                data-testid="footer-mlo-signin"
                className="hover:text-lime transition-colors"
              >
                Loan officer sign in
              </a>
              <span className="text-white/30">·</span>
              <p>Site by NEXA Mortgage LLC · Powered by Emergent</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
